/**
 * Extract dominant color from an image
 * Returns a color suitable for borders and glows
 */

export interface RGB {
  r: number;
  g: number;
  b: number;
}

/**
 * Extract dominant color from an image URL
 * Uses canvas to sample pixels and find the most vibrant/visible color
 */
export async function extractDominantColor(imageUrl: string): Promise<RGB> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Set canvas size (smaller for performance)
        canvas.width = 100;
        canvas.height = 100;
        
        // Draw image to canvas
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        // Sample pixels (sample from center region for better results)
        const sampleSize = 50;
        const startX = Math.floor((canvas.width - sampleSize) / 2);
        const startY = Math.floor((canvas.height - sampleSize) / 2);
        
        const imageData = ctx.getImageData(startX, startY, sampleSize, sampleSize);
        const pixels = imageData.data;
        
        // Calculate average color, but weight towards brighter/more saturated colors
        let totalR = 0;
        let totalG = 0;
        let totalB = 0;
        let count = 0;
        
        for (let i = 0; i < pixels.length; i += 4) {
          const r = pixels[i];
          const g = pixels[i + 1];
          const b = pixels[i + 2];
          const a = pixels[i + 3];
          
          // Skip transparent pixels
          if (a < 128) continue;
          
          // Calculate brightness and saturation
          const brightness = (r + g + b) / 3;
          const max = Math.max(r, g, b);
          const min = Math.min(r, g, b);
          const saturation = max === 0 ? 0 : (max - min) / max;
          
          // Weight towards brighter and more saturated colors
          const weight = (brightness / 255) * (0.5 + saturation * 0.5);
          
          totalR += r * weight;
          totalG += g * weight;
          totalB += b * weight;
          count += weight;
        }
        
        if (count === 0) {
          // Fallback to a default color if no valid pixels found
          resolve({ r: 254, g: 87, b: 6 }); // Orange fallback
          return;
        }
        
        const avgR = Math.round(totalR / count);
        const avgG = Math.round(totalG / count);
        const avgB = Math.round(totalB / count);
        
        // Ensure minimum brightness for visibility
        const minBrightness = 100;
        const currentBrightness = (avgR + avgG + avgB) / 3;
        
        let finalR = avgR;
        let finalG = avgG;
        let finalB = avgB;
        
        if (currentBrightness < minBrightness) {
          const factor = minBrightness / currentBrightness;
          finalR = Math.min(255, Math.round(avgR * factor));
          finalG = Math.min(255, Math.round(avgG * factor));
          finalB = Math.min(255, Math.round(avgB * factor));
        }
        
        resolve({ r: finalR, g: finalG, b: finalB });
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      // Fallback to orange if image fails to load
      resolve({ r: 254, g: 87, b: 6 });
    };
    
    img.src = imageUrl;
  });
}

/**
 * Convert RGB to rgba string
 */
export function rgbToRgba(rgb: RGB, alpha: number = 1): string {
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}
